import { useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { hiddenField, paramsEdit, paramsAdd, sOption, beneficiaryCategory, additionalPhonesCategory } from '../constants/form';
import { documentType } from '../constants/files';
import {
  isUndefinedNull,
  notUndefinedNull,
  fieldAttribute,
  isEmpty,
  getUniqueName
} from '../helpers/shared';
import { sendData, searchDependentValue, saveFieldValue } from '../helpers/base';
import { getValue } from '../helpers/form';
import { moduleCategory } from '../helpers/tier';
import { checkPEPRPEP, parentCondition } from '../helpers/conditions';
import { USER_FILES } from '../redux/types/user';
import { theme } from '../theme/theme';
import { trimLowerCase } from '../helpers/shared';

/**
 * hook to Select Component
 * @param {Object} field
 * @param {String} uniqueName
 * @returns
 */
export const useSelect = (field, uniqueName, entryProps='') => {
  const defaultValue = getValue(field);
  const { requestLanguage } = useSelector(state => state.base);
  const { action } = useSelector(state => state.tier);
  const [options, setOptions] = useState([]);
  const [idValue, setIdValue] = useState();
  const [dependencyField, setDependencyField] = useState(null);
  const { getValues, setValue } = useFormContext();
  const values = getValues();
  const areValuesUndefined = Object.values(values)?.every(value => (isUndefinedNull(value)))
  const watchDependsField = useWatch({ name: dependencyField || '' });

  useEffect(() => {
    const getDataType = async () => {
      const getValues = (isUndefinedNull(field.function_get_values)) ? [] : JSON.parse(
        field.function_get_values);
      const fieldAction = (isUndefinedNull(field.action)) ? [] : JSON.parse(
        field.action)
      setIdValue(getValues?.id);
      if ((getValues?.apply === 'start' && getValues?.type !== 'endpoint') || (
        action === paramsEdit && getValues.depends)) {
        let response = sendData(getValues?.type, getValues, true, setValue);

        if (response.find(option => option?.priority_level)) { // ? Order options by priority_level, if the prop exists
          response = response.sort((optA, optB) => optA.priority_level - optB.priority_level)
        }

        // ? Ordering options for specific fields
        if (response && uniqueName.startsWith('jobs__last_payday')) {
          response = response.sort((optA, optB) => {
            return optA.code - optB.code
          })
        }
          
        setOptions(response);

        if ((action === paramsEdit || action === paramsAdd) && getValues.depends) {
          const { result, dependsField } = await searchDependentValue(
            getValues, values, response, fieldAction);
          if (result?.length > 0) setOptions(result);
          if (notUndefinedNull(dependsField)) setDependencyField(dependsField);
        }
      }
    };

    getDataType();
  }, [field, requestLanguage, areValuesUndefined]);

  const onChange = ({ target: option }) => {
    setValue(uniqueName, option.value);
    parentCondition(option, field, setValue);
    if (notUndefinedNull(field?.id)) moduleCategory(field.id);

    if (entryProps) {
      if(entryProps.category === beneficiaryCategory) {
        const { setEntries, setWisenrollUser, setIsBlockDisabled, setCanContinue, entry } = entryProps;
        /* If the select changes, that entry is no longer valid */
        if (setEntries) {
          setEntries(prevEntries => {
            if (prevEntries.length > 1) {
              return prevEntries.map(prevEntry => {
                if (prevEntry.id === entry.id) {
                  return {...prevEntry, isValid: false };
                }
                return prevEntry;
              });
            }
            if (prevEntries.length === 1) {
              if( option.value === sOption) {
                setCanContinue(true);
              } else {
                setCanContinue(false);
              }
              return prevEntries.map(prevEntry => {
                return { ...prevEntry, isValid:false };
              });
            }
            return prevEntries;
          });
        }
        /* User info to be shown */
        if (setWisenrollUser) setWisenrollUser(null);
        /* Enabling or disabling validate button */
        if (notUndefinedNull(setIsBlockDisabled) && notUndefinedNull(setCanContinue)) {
          if (option.value !== sOption){
            setIsBlockDisabled(false);
            setCanContinue(false);
          }
          if (option.value === sOption){
            setIsBlockDisabled(true);
            setCanContinue(true);
          }
        }
      }

      if(entryProps.category === additionalPhonesCategory){
        const { setCanAddPhone, fields, getValues } = entryProps
        /* We need the values of this entry's phone number field. */
        const entryNumber = fields[0].unique_name.match(/\d+/)
        const phoneNumberField = fields.filter(field => field?.unique_name?.includes(`phone_number${entryNumber}`))[0]
        const phoneNumberName = getUniqueName(phoneNumberField)
        const phoneNumberValue = getValues(phoneNumberName)

        if (phoneNumberValue.length >= 6 && (option.value !== sOption)  && (notUndefinedNull(option.value))) {
          // console.log(option.value)
          setCanAddPhone(true)
        } else {
          setCanAddPhone(false)
        }

      }
    }
  };

  // ? For select that DO NOT have their options filled by the time getSchemaTier runs...
  useEffect(() => {
    if (values[uniqueName] && !isEmpty(values[uniqueName]) && values[
      uniqueName] !== hiddenField)
    {
      setTimeout(() => {
        setValue(uniqueName, values[uniqueName]);
      }, 3000);
    }
  }, [values]);

  useEffect(() => {
    if (notUndefinedNull(watchDependsField)) {
      if (action === paramsEdit && isEmpty(watchDependsField) && (JSON.parse(
        field.function_get_values))?.depends) {
        const select = fieldAttribute('name', uniqueName);
        let idOptions = [];
        options.forEach(item => {
          idOptions.push(item.id);
        });
        Array.from(select.options).forEach(option => {
          if (idOptions.includes(option.id)) option.hidde = true;
        });
        select.options[0].selected = true;
        setValue(uniqueName, '');
        setOptions([]);
      }
    }
  }, [watchDependsField]);

  const handleChange = (e) => {
    onChange(e, field);
  };

  return { defaultValue, idValue, onChange, options, handleChange, setValue };
};

/**
 * hook to CheckboxRadio Component
 * @param {Object} field
 * @param {Boolean} isMultiple
 * @returns
 */
export const useCheckboxRadio = (field, isMultiple = false, type) => {
  const uniqueName = `${field.unique_name}__id__${field.id}`;
  const dispatch = useDispatch();
  const { setValue, getValues } = useFormContext();
  const { requestLanguage, actualLanguage } = useSelector(state => state.base);
  const { [`data_${trimLowerCase(actualLanguage)}`] : { field: fields } = {} } = useSelector(state => state.staticData)
  const { files } = useSelector(state => state.user);
  const [options, setOptions] = useState();
  let getValuesField = field?.function_get_values;
  getValuesField = (getValuesField) ? JSON.parse(getValuesField) : [];
  const fieldToDepend = getUniqueName(fields?.filter(field => field?.unique_name === getValuesField?.field_to_depend)[0])
  let watchValue = useWatch({ name: fieldToDepend || '' })
 
  /* Getting Options */
  useEffect(() => {
    const getDataType = () => {
      let getValuesField = field?.function_get_values;
      getValuesField = (getValuesField) ? JSON.parse(getValuesField) : [];
      if (getValuesField?.apply === 'start' && getValuesField?.type !== 'endpoint') {
        const response = sendData(getValuesField?.type, getValuesField, true, setValue);
        setOptions(response);
      }
    };

    getDataType();
  }, [field, requestLanguage, setValue]);

  /* Changing options depending on fieldToDepend change */
  useEffect(() => {

    const getDataType = (changeOptionValue) => {
      if (getValuesField?.depends) {
        const response = sendData(getValuesField?.type, getValuesField, true, setValue, changeOptionValue);
        setOptions(response);
      }
    };
    
    if (getValuesField?.depends && notUndefinedNull(watchValue) && watchValue?.length && watchValue !== hiddenField && watchValue !== sOption){
      let watchedValue = watchValue
      getDataType(watchedValue)
    }

  }, [watchValue])

  const onClick = ({ target: option }) => {
    (isMultiple) ? checkPEPRPEP(option, field, setValue) : parentCondition(
      option, field, setValue);
    if (notUndefinedNull(field?.id)) moduleCategory(field.id);

    if (uniqueName?.includes(documentType)) {
      if (option.checked) {
        const newValues = [...files, option.id];
        setValue(uniqueName, newValues);
        dispatch({ type: USER_FILES, payload: { files: newValues } });
      } else {
        const newValues = files.filter(item => item !== option.id);
        setValue(uniqueName, newValues);
        dispatch({ type: USER_FILES, payload: { files: newValues } });
      }
    } else {
      const prevValues = getValues(uniqueName)

      if (field?.data_type !== 'month_radio_selection' && field?.data_type !== 'radio_selection' ){
        if(!prevValues?.includes(option.id)){
          setValue(uniqueName, [...prevValues, option.id])
        }else{
          setValue(uniqueName, prevValues?.filter(value => value !== option.id))
        }
      } else {
        setValue(uniqueName, option?.value)
      }

    }
  };

  return { onClick, options, setOptions };
};

/**
 * hook to ImageSelect component
 * @param {Object} field
 * @returns
 */
export const useImageSelect = field => {
  const { setValue, getValues } = useFormContext();
  const { first } = theme;
  const { requestLanguage } = useSelector(state => state.base);
  const [images, setImages] = useState();
  const [imagesSelect, setImagesSelect] = useState([]);

  useEffect(() => {
    const getDataType = () => {
      let getValues = field?.function_get_values;
      getValues = (getValues) ? JSON.parse(getValues) : [];
      if (getValues?.apply === 'start' && getValues?.type !== 'endpoint') {
        const response = sendData(getValues?.type, getValues, true, setValue);
        setImages(response);
      }
    };

    getDataType();
  }, [field, requestLanguage]);

  useEffect(() => {
    if (images) {
      const nameImages = field.unique_name.split('__').pop();
      const values = getValues(`${field.unique_name}__id__${field.id}`);
      if (values) {
        Array.from(images).forEach(item => {
          const index = Array.from(values).findIndex(
            elem => elem[nameImages] === item.id);
          if (index < 0) {
            item['classStatus'] = 'inactive';
            item['style'] = { border: '1px solid #eee', boxShadow: '1px 1px 1px #eee' };
          } else {
            item['classStatus'] = 'active';
            item['style'] = { border: `1px solid ${first}`, boxShadow: `1px 1px 1px ${first}` };
          }
        });
      
        setImagesSelect(values);
      }
    }
  }, [images]);

  const onClick = ({ target: image }) => {
    const { allField, newObject, index } = saveFieldValue(image, imagesSelect);

    if (image.className.includes('inactive') || (!image.className.includes('active')  && !image.className.includes('inactive'))) {
      image.classList.remove('inactive');
      image.classList.add('active');
      image.style.border = `1px solid ${first}`;
      image.style.boxShadow = `1px 1px 1px ${first}`;
      (index < 0) && allField.push(newObject);
    } else {
      image.classList.remove('active');
      image.classList.add('inactive');
      image.style.border = '1px solid #eee';
      image.style.boxShadow = '1px 1px 1px #eee';
      (index >= 0) && allField.splice(index, 1);
    }

    setValue(image.name, allField);
    setImagesSelect(allField);
  };

  return { images, onClick };
};